import type { FormRequestSchema, ReplyAttachment } from "@f2e/ui/chat/transport"

export type { ReplyAttachment } from "@f2e/ui/chat/transport"

import { FetchMethod, useCodeerApi, UrlBuilderParams } from "../useCodeerApi"
import {
  Response,
  History,
  FeedbackSystemType,
  User,
  type AIDraftToolSupport,
  type AIDraftToolSupportStatus,
  type AIDraftUnsupportedTool,
} from "@/types"
import { WorkspaceSchema, transToWorkspace, HistoryMetaSchema } from "./types"

export interface CreateHistoryResponse {
  id: number
  name?: string
  workspace_name?: string
}

export interface CreateHistoryRequest {
  name?: string
  workspace_id?: string
}

export interface HistoryResponse {
  id: number
  name: string
  updated_at?: string
  created_at?: string
  snippet?: string
  workspace_name: string
  workspace_id: string
  workspace: WorkspaceSchema
  share_type: string // Backend sends this but frontend no longer uses it
  creator: User
  meta?: HistoryMetaSchema
  external_user_id?: string
  agent_id?: string
  agent_name?: string
  total_cost_credits?: number | null
  feedback_conversation_counts?: Partial<Record<FeedbackSystemType, number>>
  can_reply?: boolean
  reply_disabled_reason?: string | null
  client_user_id?: string | null
  source_type?: History["sourceType"]
  participant_name?: string | null
  channel?: {
    id?: string | null
    name?: string | null
    type: string
  } | null
  latest_message?: {
    actor: "participant" | "agent" | "workspace_member"
    actor_name?: string | null
    preview: string
    created_at: string
  } | null
  needs_reply?: boolean
  waiting_since?: string | null
  ai_draft_tool_support?: AIDraftToolSupportResponse | null
}

export interface AIDraftToolSupportResponse {
  status: AIDraftToolSupportStatus
  agent_id?: string | null
  agent_configuration_version?: string | null
  unsupported_tools: AIDraftUnsupportedTool[]
}

export const aiDraftToolSupportResponseToToolSupport = (
  response?: AIDraftToolSupportResponse | null
): AIDraftToolSupport | undefined =>
  response
    ? {
        status: response.status,
        agentId: response.agent_id ?? undefined,
        agentConfigurationVersion:
          response.agent_configuration_version ?? undefined,
        unsupportedTools: response.unsupported_tools,
      }
    : undefined

const resolveHistoryAgentId = (resp: HistoryResponse): string | undefined => {
  if (resp.agent_id && resp.agent_id.trim()) return resp.agent_id
  const meta = resp.meta as { conversation_agent_id?: string } | undefined
  const fromMeta = meta?.conversation_agent_id
  if (fromMeta && fromMeta.trim()) return fromMeta
  return undefined
}

const resolveHistoryInitAgentId = (
  resp: Pick<HistoryResponse, "meta">
): string | undefined => {
  const meta = resp.meta as { conversation_agent_id?: string } | undefined
  const initAgentId = meta?.conversation_agent_id
  if (initAgentId && initAgentId.trim()) return initAgentId
  return undefined
}

export const historyResponseToHistory = (resp: HistoryResponse): History => {
  const workspace = transToWorkspace(resp.workspace)
  return {
    id: resp.id,
    name: resp.name,
    createdAt: resp.created_at ?? "",
    updatedAt: resp.updated_at ?? "",
    snippet: resp.snippet,
    workspace: workspace,
    creator: resp.creator,
    meta: (resp.meta ?? {}) as History["meta"],
    externalUserId: resp.external_user_id,
    agentId: resolveHistoryAgentId(resp),
    initAgentId: resolveHistoryInitAgentId(resp),
    agentName: resp.agent_name,
    totalCostCredits: resp.total_cost_credits ?? 0,
    feedbackConversationCounts: resp.feedback_conversation_counts ?? {},
    canReply: resp.can_reply,
    replyDisabledReason: resp.reply_disabled_reason ?? undefined,
    clientUserId: resp.client_user_id ?? undefined,
    sourceType: resp.source_type,
    participantName: resp.participant_name ?? undefined,
    channel: resp.channel
      ? {
          id: resp.channel.id ?? undefined,
          name: resp.channel.name ?? undefined,
          type: resp.channel.type,
        }
      : undefined,
    latestMessage: resp.latest_message
      ? {
          actor: resp.latest_message.actor,
          actorName: resp.latest_message.actor_name ?? undefined,
          preview: resp.latest_message.preview,
          createdAt: resp.latest_message.created_at,
        }
      : undefined,
    needsReply: resp.needs_reply ?? false,
    waitingSince: resp.waiting_since ?? undefined,
    aiDraftToolSupport: aiDraftToolSupportResponseToToolSupport(
      resp.ai_draft_tool_support
    ),
  }
}

export const useGetHistoriesApi = () => {
  return useCodeerApi<Response<HistoryResponse[]>>(
    "/histories",
    FetchMethod.Get
  )
}

export interface ExternalUserIdentityOptionResponse {
  client_user_id: string
  label: string
}

export const useGetExternalUserIdsApi = () => {
  return useCodeerApi<Response<ExternalUserIdentityOptionResponse[]>>(
    "/histories/external-user-ids",
    FetchMethod.Get
  )
}

export interface UpdateHistoryApiRequest {
  name: string
}

export const useUpdateHistoryApi = (id: number) => {
  return useCodeerApi<Response<HistoryResponse>, UpdateHistoryApiRequest>(
    "/histories/" + id,
    FetchMethod.Put
  )
}

export const useGetHistoryApi = (
  id: number,
  urlBuilder?: (params: UrlBuilderParams) => string
) => {
  return useCodeerApi<Response<HistoryResponse>>(
    "/histories/" + id,
    FetchMethod.Get,
    true,
    urlBuilder
  )
}

export const useDeleteHistoryApi = (id: number) => {
  return useCodeerApi<Response<unknown>>("/histories/" + id, FetchMethod.Delete)
}

export type AIDraftFormPrefillValue = string | number

export interface CreatePlatformReplyFormRequest {
  prefilled_values?: Record<string, AIDraftFormPrefillValue>
}

export interface CreatePlatformReplyRequest {
  content: string
  attachment_id?: string | null
  idempotency_key?: string
  applied_draft_id?: number
  observed_context_anchor?: {
    part_id: number
    sequence: number
  }
  form?: CreatePlatformReplyFormRequest | null
}

export type AIDraftOutcome = "generated" | "applied" | "dismissed"
export type AIDraftDismissReason =
  | "incorrect"
  | "tone_mismatch"
  | "incomplete"
  | "outdated"
  | "other"

export interface AIDraftToolActivity {
  tool_call_id: string
  sequence: number
  tool_name: string
  tool_category: string
  status: "completed" | "recoverable_failed"
  duration_ms: number
  schema_version: number
  display_args: Record<string, unknown>
  display_result: Record<string, unknown>
}

export interface AIDraftFormProposal {
  form_schema: FormRequestSchema
  prefillable_field_names: string[]
  prefilled_values: Record<string, AIDraftFormPrefillValue>
  effective_prefilled_values: Record<string, AIDraftFormPrefillValue>
}

export interface AIDraftProposedActions {
  request_form?: AIDraftFormProposal | null
}

export interface AIDraftSummaryResponse {
  id: number
  history_id?: number | null
  thread_key?: string | null
  requested_by_id: number
  requested_by_name: string
  outcome: AIDraftOutcome
  /** Missing on responses from servers predating Agent preview. */
  mode?: "human_operator" | "agent_preview"
  result_type?: "reply" | "human_handoff"
  dismiss_reason?: AIDraftDismissReason | null
  refinement_source_draft_id?: number | null
  has_generation_instruction: boolean
  has_dismiss_feedback: boolean
  context_through_part_id: number
  context_through_sequence: number
  agent_id: string
  agent_configuration_version: string
  model_id: string
  input_tokens: number
  output_tokens: number
  correlated_conversation_part_id?: number | null
  correlated_at?: string | null
  created_at: string
}

export interface AIDraftResponse extends AIDraftSummaryResponse {
  workspace_id: string
  content: string
  cost_credits?: number | null
  outcome_by_id?: number | null
  outcome_at?: string | null
  generation_instruction?: string | null
  dismiss_feedback?: string | null
  tool_activities: AIDraftToolActivity[]
  /** Null for drafts generated before form proposals, or with nothing proposed. */
  proposed_actions?: AIDraftProposedActions | null
  updated_at: string
}

export interface DismissAIDraftRequest {
  reason?: AIDraftDismissReason
  feedback?: string
}

export interface GenerateAIDraftRequest {
  instruction?: string
  refinement_source_draft_id?: number
}

export interface PlatformReplyResponse {
  id: number
  attachment?: ReplyAttachment | null
  history_id: number
  conversation_part_id: number
  client_user_id: string
  agent_id: string
  actor_user_id: number
  status: "sending" | "sent" | "failed" | string
  provider_message_id?: string | null
  error_code?: string | null
  error_message?: string | null
  source_channel?: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

export interface PlatformReplyRejectionResponse {
  code: string
  message: string
}

export const useReplyAttachmentCapabilitiesApi = () =>
  useCodeerApi<
    Response<{
      channels: Record<string, Record<string, "native" | "link">>
      extensions: Record<string, string[]>
    }>
  >("/reply-attachments/capabilities")

export const useUploadReplyAttachmentApi = () =>
  useCodeerApi<Response<ReplyAttachment>>(
    "/reply-attachments/upload",
    FetchMethod.Post
  )

export const useReplyAttachmentActionApi = (
  replyId: number,
  action: "revoke" | "resend"
) =>
  useCodeerApi<Response<PlatformReplyResponse>>(
    `/reply-attachments/${replyId}/${action}`,
    FetchMethod.Post
  )

export const useCreatePlatformReplyApi = (historyId: number) => {
  return useCodeerApi<
    Response<PlatformReplyResponse | PlatformReplyRejectionResponse>,
    CreatePlatformReplyRequest
  >(`/histories/${historyId}/platform-replies`, FetchMethod.Post)
}

export const useCreateHistoryApi = () => {
  return useCodeerApi<Response<CreateHistoryResponse>, CreateHistoryRequest>(
    "/histories",
    FetchMethod.Post
  )
}
